import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { fetchAddressSuggestions } from '../services/locationIQ';
import { getTodayDate, getMinDateForAge18 } from '../utils/formatData';
import { STATE_NAME_TO_ABBR } from '../utils/USStates';
// import Tooltip from 'bootstrap';
import '../App.css';

const FormField = ({
  name,
  label,
  type,
  options = [],
  required,
  colSpan = 6,
  colClass,
  formatPattern,
  backendErrors = [],
  maxlength,
  autocomplete,
  placeholder,
  helperText,
  labelIcon,
  validationPattern,
  validationMessage,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  // const [localValue, setLocalValue] = useState('');
  const SSN_FIELDS = ['ssn', 'ssn_confirm', 'co_borrower_ssn', 'co_borrower_ssn_confirm'];
const digitsOnly = (s = "") => (s || "").replace(/\D/g, "").slice(0, 9);


  const [isFocused, setIsFocused] = React.useState(false);

  const form = useFormContext();
  if (!form) return null;

  const {
    register,
    setValue,
    formState: { errors, touchedFields, dirtyFields }
  } = form;

 

  // const hasError = !!(errors[name] || backendErrors.find((err) => err.path === name));
  const isTouched = touchedFields[name];
  // const isDirty = dirtyFields[name];

  const getInputClass = (base) => {
    if (errors[name]) return `${base} is-invalid`;
    if (isTouched && !errors[name]) return `${base} is-valid`;
    return base;
  };
  

  const getDateConstraints = () => {
    const today = getTodayDate();
    const ageLimit = getMinDateForAge18();
    const addressSince = form.watch('address_since');
    const employerSince = form.watch('employer_since');
    const coBorrowerEmployerSince = form.watch('co_borrower_employer_since');

    if (['dob', 'co_borrower_dob'].includes(name)) return { max: ageLimit };
    if (
      ['address_since', 'employer_since', 'co_borrower_employer_since', 'drivers_license_issue_date', 'co_borrower_drivers_license_issue_date', 'co_borrower_address_since'].includes(name)
    ) return { max: today };
    if (
      ['drivers_license_expire_date', 'co_borrower_drivers_license_expire_date', 'next_payment_date', 'next_payment_date_2', '' ].includes(name)
    ) return { min: today };

    if (['past_movein_date', 'past_moveout_date'].includes(name)) {
      return {
        max: addressSince ? new Date(addressSince).toISOString().split('T')[0] : today
      };
    }
    if (['past_hire_date', 'past_last_day_of_employment_date'].includes(name)) {
      return {
        max: employerSince ? new Date(employerSince).toISOString().split('T')[0] : today
      };
    }

    if (['co_borrower_past_hire_date', 'co_borrower_past_last_day_of_employment_date'].includes(name)) {
      return {
        max: coBorrowerEmployerSince ? new Date(coBorrowerEmployerSince).toISOString().split('T')[0] : today
      };
    }
    return {};
  };

  
  const formatPhoneNumber = (value) => {
    if (!value) return ''; 
    const cleaned = (value || '').replace(/[^\d]/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const formatSSN = (value) => {
    const cleaned = (value || '').replace(/[^\d]/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 5) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 9)}`;
  };

  // store canonical value like 123-45-6789
  const formatSSNFromDigits = (digs = "") => {
    const d = digitsOnly(digs);
    const a = d.slice(0, 3), b = d.slice(3, 5), c = d.slice(5, 9);
    return [a, b, c].filter(Boolean).join("-");
  };

  const maskSSNFromDigits = (digs = "") => {
    const d = digitsOnly(digs);
    const n = d.length;
    if (n === 0) return "";

    const a = Math.min(n, 3);              
    const b = n > 3 ? Math.min(n - 3, 2) : 0;  
    const c = n > 5 ? Math.min(n - 5, 4) : 0;  

    const partA = "*".repeat(a);
    const dash1 = n >= 4 ? "-" : "";       
    const partB = "*".repeat(b);
    const dash2 = n >= 6 ? "-" : "";       
    const partC = d.slice(5, 5 + c);       

    return `${partA}${dash1}${partB}${dash2}${partC}`;
  };


  const formatCurrency = (value) => {
    const digits = (value || '').replace(/[^\d]/g, '');
    if (!digits) return '';
  
    const number = parseInt(digits, 10);
    if (isNaN(number)) return '';
  
    return number.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };
  
  
  
  const currencyFields = [
    "monthly_income",
    "monthly_home_payment",
    "other_expenses",
    "current_monthly_payment",
    "current_payoff",
    "co_borrower_monthly_income",
    "co_borrower_monthly_home_payment",
    "co_borrower_other_expenses",
    "co_borrower_current_monthly_payment",
    "co_borrower_current_payoff",
    'co_borrower_gross_income',
   
  ];
  const phoneFields = [
    'phone_1',
    'employer_phone_1',
    'co_borrower_phone_1',
    'co_borrower_employer_phone_1',
  ];

  const basePrefix = name.replace(/address_1$/, '');
  const cityField = `${basePrefix}city`;
  const stateField = `${basePrefix}state`;
  const zipField = `${basePrefix}zip`;


  const renderField = () => {
    const commonProps = {
      className: getInputClass('form-control'),
      //  ...register(name, {
      //   required: required ? `${label || name} is required` : false,
      //   ...(validationPattern ? {
      //     pattern: {
      //       value: new RegExp(validationPattern),
      //       message: validationMessage || `${label || name} is invalid`,
      //     }
      //   } : {}),
      //  }),
      ...register(name),
      maxLength: phoneFields.includes(name) ? 12 : maxlength,
      placeholder: placeholder,
    };

    
  
    if (type === 'select') {
      const currentValue = form.watch(name) || '';
      return (
        <select
          {...commonProps}
          className={`${getInputClass('form-select')} ${currentValue === '' ? 'placeholder-active' : ''}`}
          value={currentValue}
        >
          <option value="" disabled>{placeholder || `Choose ${label}`}</option>
          {options.map((opt, idx) => (
            <option key={idx} value={opt.value || opt}>
              {opt.label || opt}
            </option>
          ))}
        </select>
      );
    }
  
    if (type === 'radio') {
      return (
        <div className="radio-group-container d-flex flex-wrap gap-4 align-items-center">
          {options.map((opt, idx) => (
            <div className="form-check d-flex align-items-center" key={idx}>
              <input
                className="form-check-input me-2"
                type="radio"
                id={`${name}_${idx}`}
                value={opt.value || opt}
                {...register(name)}
              />
              <label className="form-check-label m-0" htmlFor={`${name}_${idx}`}>
                {opt.label || opt}
              </label>
            </div>
          ))}
        </div>
      );
    }
  
    if (type === 'textarea') {
      return <textarea {...commonProps} rows="4" placeholder={placeholder || " "} />;
    }
  
    if (type === 'date') {
      const { min, max } = getDateConstraints();
      return <input {...commonProps} type="date" min={min} max={max} />;
    }
    
    if (type === "time_native_ampm") {
      const to12h = (hhmm) => {
        if (!hhmm) return "";
        const [hStr, mStr] = hhmm.split(":");
        const h = Number(hStr);
        const ap = h >= 12 ? "PM" : "AM";
        const h12 = ((h + 11) % 12) + 1;
        return `${String(h12).padStart(2, "0")}:${mStr} ${ap}`;
      };
    
      const to24h = (val12) => {
        // expects "08:15 PM"
        if (!val12) return "";
        const [time, ap] = val12.trim().split(" ");
        if (!time || !ap) return "";
        let [hh, mm] = time.split(":");
        let h = Number(hh);
        if (ap === "AM" && h === 12) h = 0;
        if (ap === "PM" && h !== 12) h += 12;
        return `${String(h).padStart(2, "0")}:${mm}`;
      };
    
      return (
        <Controller
          name={name}
          control={form.control}
          render={({ field }) => (
            <input
              className={getInputClass("form-control")}
              type="time"
              step={900} // 15-min steps (change if you want)
              value={to24h(field.value)}
              onChange={(e) => field.onChange(to12h(e.target.value))}
            />
          )}
        />
      );
    }


    if (type === "call_schedule") {

      const to12hFrom24 = (hhmm) => {
        if (!hhmm) return "";
        const [hStr, mStr] = hhmm.split(":");
        const h = Number(hStr);
        const ap = h >= 12 ? "PM" : "AM";
        const h12 = ((h + 11) % 12) + 1;
        return `${String(h12).padStart(2, "0")}:${mStr} ${ap}`;
      };
    
      const updateCallSchedule = (rule, time24) => {
    
        let text = "";
    
        if (rule === "Anytime" && time24)
          text = `Anytime ${to12hFrom24(time24)}`;
        else if (rule && time24)
          text = `${rule} ${to12hFrom24(time24)}`;
        else
          text = rule || "";
    
        // update actual DB field
        form.setValue(name, text);
      };
    
      return (
        <>
          {/* hidden real field */}
          <input type="hidden" {...register(name)} />
    
          <div className="d-flex gap-3 align-items-center">
    
            <select
              className="form-select"
              {...register("call_availability_rule")}
              onChange={(e) => {
                const rule = e.target.value;
                const time = form.getValues("call_availability_time");
                updateCallSchedule(rule, time);
              }}
            >
              <option value="">Choose</option>
              <option>Anytime</option>
              <option>After</option>
              <option>Before</option>
              <option>Weekdays only</option>
              <option>Weekends</option>
            </select>
    
            <Controller
              name="call_availability_time"
              control={form.control}
              render={({ field }) => (
                <input
                  type="time"
                  className="form-control"
                  step={900}
                  value={field.value || ""}
                  onChange={(e) => {
                    field.onChange(e);
                    const rule = form.getValues("call_availability_rule");
                    updateCallSchedule(rule, e.target.value);
                  }}
                />
              )}
            />
    
          </div>
        </>
      );
    }
    
    

  

if (currencyFields.includes(name)) {
  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field }) => {
        // Format value for display
        const formatForDisplay = (value) => {
          if (!value) return '';
          const num = parseFloat(value);
          if (isNaN(num)) return '';
          
          return num.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        };
        
        return (
          <input
            className={getInputClass('form-control')}
            type="text"
            value={isFocused ? (field.value || '') : formatForDisplay(field.value)}
            onChange={(e) => {
              const val = e.target.value;
              
              // Remove all non-numeric characters except decimal point
              const cleaned = val.replace(/[^\d.]/g, '');
              
              // Ensure only one decimal point
              const parts = cleaned.split('.');
              let formatted = parts[0];
              if (parts.length > 1) {
                // Limit to 2 decimal places
                formatted += '.' + parts[1].slice(0, 2);
              }
              
              // Store the clean numeric value
              field.onChange(formatted);
            }}
            onFocus={() => {
              setIsFocused(true);
            }}
            onBlur={() => {
              setIsFocused(false);
              // Ensure value has 2 decimal places when stored
              if (field.value && !isNaN(field.value)) {
                const num = parseFloat(field.value);
                field.onChange(num.toFixed(2));
              }
            }}
            placeholder={isFocused ? '0.00' : '$0.00'}
          />
        );
      }}
    />
  );
}
    
// NEW: MASKED SSN HANDLER (runs before generic input)
if (SSN_FIELDS.includes(name)) {
  return (
    <Controller
      name={name}
      control={form.control}
      defaultValue=""
      render={({ field }) => {
        const rawDigits = digitsOnly(field.value);          // only the numbers (0..9), max 9
        const displayMasked = maskSSNFromDigits(rawDigits); // what we show to the user

        return (
          <input
            className={getInputClass('form-control')}
            type="text"
            value={displayMasked}
            inputMode="numeric"
            autoComplete="off"
            onKeyDown={(e) => {
              const allowNav = ['Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
              if (allowNav.includes(e.key)) return;

              if (e.key === 'Backspace') {
                e.preventDefault();
                const next = rawDigits.slice(0, -1);
                field.onChange(formatSSNFromDigits(next)); // store real formatted value: 123-45-6789
                return;
              }

              if (/^\d$/.test(e.key)) {
                e.preventDefault();
                if (rawDigits.length >= 9) return;
                const next = rawDigits + e.key;
                field.onChange(formatSSNFromDigits(next)); // store real formatted
                return;
              }

              // Block everything else (letters, spaces, etc.)
              e.preventDefault();
            }}
            onPaste={(e) => {
              e.preventDefault();
              const pasted = digitsOnly(e.clipboardData.getData('text'));
              field.onChange(formatSSNFromDigits(pasted));
            }}
          />
        );
      }}
    />
  );
}


    // Generic Input Handler
    return (
      <div className="position-relative">
        <input
          {...commonProps}
          type={type}
          value={form.watch(name) || ''}
          onChange={async (e) => {
            const val = e.target.value;
  
            if (phoneFields.includes(name)) {
              const formatted = formatPhoneNumber(val);
              setValue(name, formatted, { shouldValidate: true, shouldDirty: true });
              return;
            }
  
            if (
              ['ssn', 'ssn_confirm', 'co_borrower_ssn', 'co_borrower_ssn_confirm'].includes(name) &&
              formatPattern === '###-##-####'
            ) {
              setValue(name, formatSSN(val), { shouldValidate: true });
              return;
            }
  
            setValue(name, val, { shouldValidate: true, shouldTouch: true });
            form.trigger(name); // ensure Yup re-runs the conditional

  
            if (autocomplete === 'locationIQ' && name.endsWith('address_1') && val.length > 3) {
              const results = await fetchAddressSuggestions(val);
              setSuggestions(results);
              setShowDropdown(true);
            }
          }}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          onFocus={() => setShowDropdown(name.endsWith('address_1') && autocomplete === 'locationIQ')}
        />
  
        {showDropdown && name.endsWith('address_1') && suggestions.length > 0 && (
          <ul className="list-group position-absolute z-3 w-100 mt-1" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {suggestions.map((item, idx) => {
              const addr = item.address;
              const street = [addr.house_number, addr.road].filter(Boolean).join(' ');
              const city = addr.city || addr.town || addr.village || '';
              const stateAbbr = STATE_NAME_TO_ABBR[addr.state] || '';
              const zip = addr.postcode || '';
              const label = [street, city, stateAbbr].filter(Boolean).join(', ');
  
              return (
                <li
                  key={idx}
                  className="list-group-item list-group-item-action"
                  onClick={() => {
                    setValue(name, street, { shouldValidate: true });
                    setValue(cityField, city);
                    setValue(stateField, stateAbbr);
                    setValue(zipField, zip);
                    setShowDropdown(false);
                  }}
                >
                  {label}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };
  
  const fieldErrors = errors[name] || backendErrors.find((err) => err.path === name);
  // const responsiveCol = colClass || `col-xl-${colSpan} col-lg-${colSpan} col-md-${colSpan} col-sm-12`;
  const responsiveCol = colClass || `col-xl-${colSpan} col-lg-${colSpan} col-md-12 col-sm-12`;



  return (
    <div className={`form-group mb-3 ${responsiveCol}`}>
    
    <label htmlFor={name} className="form-label d-flex align-items-center gap-2">
      {typeof label === 'function' ? label(form.getValues()) :label}
      {required && <span className="text-danger">*</span>}
      {labelIcon && (
        <span
          className="info-icon"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-trigger="click focus"
          title={typeof labelIcon === 'string' ? 'More info' : labelIcon.tooltip}
        >
          <i className={typeof labelIcon === 'string' ? labelIcon : labelIcon.icon} style={{ fontSize: '0.85rem', cursor: 'pointer', color: '#666' }}></i>
        </span>
      )}
    </label>


      {renderField()}
      {helperText && (
        <small className="form-text text-muted two-lines">
          {helperText}
        </small>
      )}
      {fieldErrors && (
        <p className="text-danger" style={{ fontSize: '0.875rem' }}>
          {fieldErrors.message || fieldErrors.msg || 'This field is required'}
        </p>
      )}
    </div>
  );
};

export default FormField;
